import { useEffect } from 'react';
import { getIefeString } from '../../../../features/activities/hotelSearch/util/worker/utils';
import { AgentInboxModel } from '../../table/types/types';
import { useAgentInboxItemStore } from '../../stores/useAgentInboxItemStore';
import { useAgentDrawerStore } from '../../../../features/agentDrawer/stores/agentDrawerStore';
import { AgentDrawerModel } from '../../../../features/agentDrawer/types/types';
import { useFolioItemStore } from '../../../folio/stores/useFolioItemStore';

type BatchUpdateRequestType = {
  Id: number;
  [key: string]: any;
};

export enum EventType {
  InboxUpdate = 0,
  InboxRemove = 1,
  AgentUpdate = 2,
  AgentRemove = 3,
  FolioUpdate = 4,
  FolioRemove = 5
}

type TypedBatchUpdateResponse = {
  type: EventType;
  items: BatchUpdateRequestType[];
};

const workerScript = URL.createObjectURL(
  new Blob(
    [
      '(()=>{',
      getIefeString(function () {
        enum EventType {
          InboxUpdate = 0,
          InboxRemove = 1,
          AgentUpdate = 2,
          AgentRemove = 3,
          FolioUpdate = 4,
          FolioRemove = 5
        }

        const BatchThreshold = 100;
        const FallbackTimeout = 500;

        // Type-based batching: separate pending maps and fallback timers for each event type
        const pendingMaps = new Map<EventType, Map<number, any>>();
        const fallbackTimers = new Map<EventType, any>();

        function getPendingMapForType(type: EventType): Map<number, any> {
          if (!pendingMaps.has(type)) {
            pendingMaps.set(type, new Map());
          }
          return pendingMaps.get(type)!;
        }

        function startFallbackTimerForType(type: EventType) {
          // Clear existing fallback timer if any
          const existingTimer = fallbackTimers.get(type);
          if (existingTimer) clearTimeout(existingTimer);

          // Set new fallback timer (500ms from now, not after silence)
          const newTimer = setTimeout(() => {
            flushPendingForType(type);
            fallbackTimers.delete(type);
          }, FallbackTimeout);

          fallbackTimers.set(type, newTimer);
        }

        onmessage = (e: MessageEvent<{ type: EventType; data: any }>) => {
          const { type, data } = e.data;
          const update = data;

          // Get type-specific pending map
          const pending = getPendingMapForType(type);

          // If this is the first item, start the fallback timer
          const wasEmpty = pending.size === 0;

          pending.set(update.Id, update);

          // Start fallback timer only if pending was empty (first item)
          if (wasEmpty) {
            startFallbackTimerForType(type);
          }

          // Check if we've reached the batch threshold
          if (pending.size >= BatchThreshold) {
            // Clear the fallback timer since we're flushing now
            const existingTimer = fallbackTimers.get(type);
            if (existingTimer) {
              clearTimeout(existingTimer);
              fallbackTimers.delete(type);
            }

            flushPendingForType(type);
          }
        };

        function flushPendingForType(type: EventType) {
          const pending = getPendingMapForType(type);

          if (pending.size > 0) {
            // Send typed response
            postMessage({
              type: type,
              items: Array.from(pending.values())
            });

            pending.clear();
          }
        }
      }),
      '})()'
    ],
    { type: 'application/javascript' }
  )
);

export const batchUpdatesRef = (() => {
  let instance: Worker | null = null;

  function createHotelSearchWorker(debug?: boolean) {
    if (debug) {
      fetch(workerScript).then((r) => {
        r.blob().then((x) => {
          x.text().then((y) => {
            //to debug easier copy and past this somewhere and play around with the execution
            // eslint-disable-next-line no-console
            console.log(y);
          });
        });
      });
    }
    return new Worker(workerScript);
  }

  return {
    getInstance(debug?: boolean) {
      if (!instance) {
        instance = createHotelSearchWorker(debug);
      }
      return instance;
    }
  };
})();

/**
 * Enhanced BatchUpdateWorker with multi-type event routing
 * Routes different event types to their appropriate stores:
 * - InboxUpdate/InboxRemove -> AgentInboxItemStore
 * - FolioUpdate/FolioRemove -> AgentFolioItemStore
 * - AgentUpdate/AgentRemove -> AgentDrawerStore
 */
export const EnhancedBatchUpdateWorker = () => {
  // Inbox store methods
  const inboxUpdate = useAgentInboxItemStore((r) => r.processUpdates);
  const inboxRemove = useAgentInboxItemStore((r) => r.handleBulkRemove);

  // Folio store methods
  const folioUpdate = useFolioItemStore((r) => r.handleBulkUpdate);
  const folioRemove = useFolioItemStore((r) => r.handleBulkRemove);

  // Agent drawer store methods
  const agentUpdate = useAgentDrawerStore((r) => r.handleBulkUpdate);
  const agentRemove = useAgentDrawerStore((r) => r.handleBulkRemove);

  useEffect(() => {
    const worker = batchUpdatesRef.getInstance(false);

    function handler({ data }: MessageEvent<TypedBatchUpdateResponse>) {
      switch (data.type) {
        case EventType.InboxUpdate:
          inboxUpdate(data.items as AgentInboxModel[]);
          break;

        case EventType.InboxRemove:
          inboxRemove(data.items.map((item) => item.Id));
          break;

        case EventType.FolioUpdate:
          folioUpdate(data.items as any);
          break;

        case EventType.FolioRemove:
          folioRemove(data.items.map((item) => item.Id));
          break;

        case EventType.AgentUpdate:
          agentUpdate(data.items as AgentDrawerModel[]);
          break;

        case EventType.AgentRemove:
          agentRemove(data.items.map((item) => item.Id));
          break;

        default:
          break;
      }
    }

    worker.addEventListener('message', handler);
    return () => {
      worker.removeEventListener('message', handler);
    };
  }, [inboxUpdate, inboxRemove, folioUpdate, folioRemove, agentUpdate, agentRemove]);

  return <></>;
};
